import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";
import { analytics, identify, reset } from "@/lib/analytics";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("ac_token", data.token);
    setUser(data.user);
    // Identity is attached here, at the auth boundary: without it the
    // anonymous session that led to signup never joins the user, and on a
    // shared browser one person's events land on the next.
    identify(data.user.user_id, { email: data.user.email, name: data.user.name });
    analytics.login("password");
    return data.user;
  };

  const register = async (name, email, password, inviteToken = "") => {
    // Only read by the server while INVITE_ONLY is on, and ignored otherwise.
    // Sent unconditionally so the open-signup path needs no separate branch.
    const { data } = await api.post("/auth/register", {
      email, password, name, invite_token: inviteToken || "",
    });
    localStorage.setItem("ac_token", data.token);
    setUser(data.user);
    identify(data.user.user_id, { email: data.user.email, name: data.user.name });
    analytics.signup("password");
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("ac_token");
    setUser(null);
    reset();
  };

  const refresh = checkAuth;

  return (
    <AuthCtx.Provider value={{ user, setUser, loading, login, register, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
